import React, { useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./Modal.css";
import "./PromptModal.css";
import "./TripAI.css";
import "./DestinationsScreen.css";
import PromptModal from "./PromptModal";
import MultiSelectModal from "./MultiSelectModal";
import DestinationCard from "./DestinationCard";
import ExploreDestinationsFlow from "./ExploreDestinationsFlow";
import AIDestinationsFlow from "./AIDestinationsFlow";
import ScheduleDestinationsFlow, { deriveCandidatesFromEvents, effectiveDestinations } from "./ScheduleDestinationsFlow";
import PasteDestinationsFlow from "./PasteDestinationsFlow";
import ImportConfirmationModal from "./ImportConfirmationModal";
import Common, { AUTHORIZATION_LEVEL } from "../../Common/Common";
import { overlapError, tripLengthError, cleanDestination, editDestination } from "../../Common/DestinationValidation";
import { countries } from "../../Data/Countries";
import { citiesByCountry } from "../../Data/Cities";
import { slice1Actions } from "../../store/reducer";

const OTHER_CITY_VALUE = "__other__";

// A destination with no city is a placeholder day ("Sea Day", "Travel Day")
// rather than a real place — country holds the free-text label instead of an
// actual country in that case. noPlace is edit-only UI state (derived here,
// stripped again by cleanDestination before persisting) so a freshly-added
// destination that simply hasn't had its city picked yet isn't mistaken for
// one — see memory: project_destinations_first_redesign_idea.
const withNoPlace = (d) => ({ ...d, noPlace: d.city === "" && !!d.country });

// effectiveDestinations() returns [] for a genuinely blank guide (no saved
// destinations, no Main-schedule events to derive from) — matches mobile's
// DestinationsScreen.js _defaultDestination(): an empty card is a much
// friendlier landing than a blank screen with nothing to click.
const defaultDestination = (GuideSettings) => {
  const attrs = window.guide_obj?.attributes;
  const startH = GuideSettings?.schedule_day_start ?? 9;
  const endH = GuideSettings?.schedule_day_end ?? 20;
  const tomorrow = Common.LuxonDateFromJSDate(new Date()).plus({ days: 1 }).startOf("day");
  return {
    id: Date.now(),
    city: attrs?.cities?.[0] || "",
    country: attrs?.countries?.[0] || "",
    dateStart: tomorrow.set({ hour: startH }),
    dateEnd: tomorrow.set({ hour: endH }),
  };
};

// suppressFindPlacesOffer — set by callers that ARE themselves a
// find-places wizard (BuildMyTripWizard.js/TripAI.js's own "Edit
// Destinations" link): the whole point of returning to them after Save is
// already finding places, so offering to open ANOTHER Build My Trip on top
// would be redundant/confusing. Only the standalone entry points
// (MobileNavBar.js's Destination tab, Places.js's empty-guide hint) want the
// offer, since those genuinely have nowhere else lined up to go next.
// guideCities — optional { [country]: [city, …] } (Build My Trip's "From
// existing guides"): the country and city pickers then offer ONLY places a
// guide exists for, and "Type a different city…" is left out, so nobody
// plans a trip around a destination that has no guide. 2026-10-13.
// datesOnly (2026-10-17): only the dates can change — Build My Trip's "This
// guide's places", where the cities come from the guide's places.
const DestinationsScreen = ({ onHide, suppressFindPlacesOffer = false, guideCities = null, datesOnly = false }) => {
  const dispatch = useDispatch();
  const GuideSettings = useSelector((state) => state.GuideSettings);
  const user_info = useSelector((state) => state.user_info);
  const Authorization = useSelector((state) => state.Authorization);
  const isGuestUser = user_info?.uid == null || user_info?.uid === "guest" || Authorization === AUTHORIZATION_LEVEL.GUEST;

  // effectiveDestinations() is THE single canonical source (also used by
  // TripAI.js/BuildMyTripWizard.js) — persisted, else derived from the Main
  // schedule, else one single default. Nothing is written back to the guide
  // until the user hits Save here, per the deferred-migration design (see
  // memory: project_destinations_first_redesign_idea).
  const [destinations, setDestinations] = useState(() => {
    const initial = effectiveDestinations();
    return (initial.length > 0 ? initial : [defaultDestination(GuideSettings)]).map(withNoPlace);
  });
  const [editingDestIdx, setEditingDestIdx] = useState(0);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [showCityPicker, setShowCityPicker] = useState(false);
  const [showCityTypePrompt, setShowCityTypePrompt] = useState(false);
  const [cityTypePromptValue, setCityTypePromptValue] = useState("");
  const [showPasteDestinations, setShowPasteDestinations] = useState(false);
  const [showScheduleImport, setShowScheduleImport] = useState(false);
  const [scheduleImportSourceEvents, setScheduleImportSourceEvents] = useState(null);
  const [showImportConfirmation, setShowImportConfirmation] = useState(false);
  const [showExplore, setShowExplore] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [error, setError] = useState("");
  const [showFindPlacesOffer, setShowFindPlacesOffer] = useState(false);
  // Whether the guide had no places when this screen opened (2026-10-24,
  // matches mobile): an import from a confirmation adds its ports as places
  // before Save, and counting those hid the "Find places" offer.
  const hadNoPlacesAtOpen = useRef((window.guide_obj?.places?.placesList || []).length === 0);
  // This offer's own Pace choice — its "Let Me Pick" variant autoStarts
  // straight past BuildMyTripWizard's own setup screen (and the Pace
  // picker on it), so this offer needs its own. Defaults to Relaxed (1.3),
  // not Normal (1) — see BuildMyTripWizard.js.
  const [paceMultiplier, setPaceMultiplier] = useState("1.3");

  const onChangeDestStart = (idx, date) => {
    const d = Common.LuxonDateFromJSDate(date);
    // built cleared — see onCountryPicked's comment; the schedule that was
    // built covered the OLD date range, so a changed start date needs
    // Build My Trip/TripAI to reconsider this destination, not skip it.
    setDestinations((prev) => prev.map((dest, i) => (i === idx ? editDestination(dest, { dateStart: d, dateEnd: dest.dateEnd < d ? d : dest.dateEnd }) : dest)));
  };

  const onChangeDestEnd = (idx, date) => {
    const d = Common.LuxonDateFromJSDate(date);
    // built cleared — see onChangeDestStart's comment. An end moved before
    // the start pulls the start back with it (mirror of onChangeDestStart).
    setDestinations((prev) => prev.map((dest, i) => (i === idx ? editDestination(dest, { dateEnd: d, dateStart: dest.dateStart > d ? d : dest.dateStart }) : dest)));
  };

  const addDestination = () => {
    setDestinations((prev) => {
      const startH = GuideSettings?.schedule_day_start ?? 9;
      const endH = GuideSettings?.schedule_day_end ?? 20;
      const last = prev[prev.length - 1];
      const newStart = last ? last.dateEnd.plus({ days: 1 }).startOf("day").set({ hour: startH }) : Common.LuxonDateFromJSDate(new Date()).startOf("day").set({ hour: startH });
      const newEnd = newStart.set({ hour: endH });
      return [...prev, { id: Date.now(), city: "", country: last?.country || "", dateStart: newStart, dateEnd: newEnd, noPlace: false }];
    });
  };

  const toggleNoPlace = (idx) => {
    setDestinations((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, noPlace: !d.noPlace, city: "", country: "" } : d))
    );
  };

  // noSearch — a waypoint destination: a real, geocoded place kept on the
  // map/schedule but excluded from Build My Trip's attraction search. See
  // memory: project_waypoint_destination_type_idea.
  const toggleNoSearch = (idx) => {
    setDestinations((prev) => prev.map((d, i) => (i === idx ? { ...d, noSearch: !d.noSearch } : d)));
  };

  const onChangeWaypointLabel = (idx, value) => {
    setDestinations((prev) => prev.map((d, i) => (i === idx ? { ...d, waypointLabel: value } : d)));
  };

  const onChangeDestLabel = (idx, value) => {
    setDestinations((prev) => prev.map((d, i) => (i === idx ? { ...d, country: value } : d)));
  };

  const removeDestination = (idx) => {
    setDestinations((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeAllDestinations = () => {
    if (!window.confirm("This removes every destination from this guide. Continue?")) return;
    setDestinations([]);
  };

  const moveDestination = (idx, delta) => {
    setDestinations((prev) => {
      const target = idx + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = prev.slice();
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const openCountryPicker = (idx) => {
    setEditingDestIdx(idx);
    setShowCountryPicker(true);
  };

  const onCountryPicked = (selected) => {
    const country = selected?.[0]?.value;
    setShowCountryPicker(false);
    if (!country) return;
    setDestinations((prev) =>
      // built (see cleanDestination's comment) means "this destination's
      // places were already searched and added to the schedule" — that no
      // longer holds once the destination's own country changes, so an
      // edited destination doesn't get silently skipped by Build My Trip/
      // TripAI as if it were the place it used to be. Found 2026-09-27 —
      // reported as "already added" still showing after changing the city.
      // editDestination also keeps what was built (builtAs, 2026-10-23), so
      // Build My Trip replaces those events instead of leaving them.
      prev.map((d, i) => (i === editingDestIdx ? editDestination(d, { country, city: country !== d.country ? "" : d.city }) : d))
    );
  };

  const openCityPicker = (idx) => {
    setEditingDestIdx(idx);
    const dest = destinations[idx];
    if (!dest.country) {
      setShowCountryPicker(true);
      return;
    }
    // guideCities mode: no free typing. A country with no guide (possible
    // for a destination set before this mode was chosen) goes back to the
    // country picker instead.
    if (guideCities) {
      if ((guideCities[dest.country] || []).length === 0) setShowCountryPicker(true);
      else setShowCityPicker(true);
      return;
    }
    const cityList = citiesByCountry[dest.country] || [];
    if (cityList.length === 0) {
      setCityTypePromptValue("");
      setShowCityTypePrompt(true);
      return;
    }
    setShowCityPicker(true);
  };

  const onCityPicked = (selected, searchText) => {
    const value = selected?.[0]?.value;
    setShowCityPicker(false);
    if (!value) return;
    if (value === OTHER_CITY_VALUE) {
      // The user typically already typed the city into the picker's own
      // search box to find it, only to discover it isn't in the curated
      // list — pre-fill the free-text prompt with that instead of making
      // them retype it. Found/added 2026-09-14.
      setCityTypePromptValue(searchText || "");
      setShowCityTypePrompt(true);
      return;
    }
    // built cleared — see onCountryPicked's comment; a changed city
    // invalidates whatever was previously built for the old one.
    setDestinations((prev) => prev.map((d, i) => (i === editingDestIdx ? editDestination(d, { city: value }) : d)));
  };

  const saveTypedCity = async (inputs) => {
    const raw = (inputs?.[0]?.value || "").trim();
    setShowCityTypePrompt(false);
    if (raw === "") return;
    // built cleared — see onCountryPicked's comment.
    setDestinations((prev) => prev.map((d, i) => (i === editingDestIdx ? editDestination(d, { city: raw }) : d)));
  };

  const applyDestinationsList = (newDestinations) => {
    setDestinations(newDestinations.map(cleanDestination).map(withNoPlace));
    setEditingDestIdx(0);
    setShowExplore(false);
    setShowAI(false);
    setShowScheduleImport(false);
    setShowPasteDestinations(false);
    setError("");
  };

  const handleSave = () => {
    // An empty list is a legitimate save now — matches mobile's
    // DestinationsScreen.js (deleting down to zero, e.g. via the new
    // "Delete All", is allowed; there's simply nothing left to validate
    // below). Previously blocked here with "Add at least one destination.",
    // which made "delete everything" a dead end with no way to actually
    // save that state. Found 2026-09-04.
    if (destinations.some((d) => (d.noPlace ? !d.country : !d.city || !d.country))) {
      setError("Every destination needs a country and city (or a label for placeholder days).");
      return;
    }
    const overlap = overlapError(destinations);
    if (overlap) {
      setError(overlap);
      return;
    }
    const lengthError = tripLengthError(destinations);
    if (lengthError) {
      setError(lengthError);
      return;
    }
    window.guide_obj.destinations = destinations.map(cleanDestination);
    // Flag this trip for persistence on sign-in — matches TripAI.js/
    // BuildMyTripWizard.js's own equivalent flag. Without this, a guest who
    // sets destinations here and signs in BEFORE ever running a full
    // TripAI/Build My Trip generation (the only other places this flag got
    // set) would have this edit silently discarded — findUserByUIDCallback
    // only preserves the guest's in-memory guide when this flag is set,
    // otherwise it loads the signed-in account's own last guide instead.
    // Found 2026-08-28 — a guest set both destinations and preferences,
    // signed in to save, and lost both.
    if (isGuestUser) window.guest_generated_unsaved_trip = true;
    dispatch(slice1Actions.setGuide({ guide: null }));
    // Perfect moment to offer finding places for what was just saved — but
    // only when: the caller isn't itself a find-places wizard already
    // (suppressFindPlacesOffer); the guide genuinely has none yet (an
    // already-populated guide just being re-edited here shouldn't get an
    // unsolicited offer every time); and there's an actual place to search
    // for (a placeholder-only save has nothing to find).
    const hasPlaces = !hadNoPlacesAtOpen.current;
    const hasRealDestination = destinations.some((d) => d.city);
    if (!suppressFindPlacesOffer && !hasPlaces && hasRealDestination) {
      setShowFindPlacesOffer(true);
      return;
    }
    onHide();
  };

  const acceptFindPlacesOffer = (pickPlacesAuto) => {
    window.dispatchEvent(new CustomEvent("openBuildMyTripWizard", { detail: { pickPlacesAuto, paceMultiplier } }));
    onHide();
  };

  if (showFindPlacesOffer) {
    return (
      <div className="tai-paste-overlay" onClick={onHide}>
        <div className="tai-paste-modal" onClick={(e) => e.stopPropagation()}>
          <p className="tai-paste-title">Find Places?</p>
          <p className="tai-paste-hint">
            Destinations saved. Want to find places for them now?
          </p>
          {/* "Let Me Pick" autoStarts straight past BuildMyTripWizard's own
              setup screen (and the Pace picker on it), so this offer needs
              its own — otherwise that flow never gets to choose a pace at
              all. Ported to mobile's DestinationsScreen.js the same day. */}
          <span className="tai-section-label">Pace</span>
          <div className="tai-input-row">
            <select className="tai-select" value={paceMultiplier} onChange={(e) => setPaceMultiplier(e.target.value)}>
              {Common.PACE_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          <div className="tai-paste-actions">
            <button className="tai-cancel-btn" onClick={onHide}>Not now</button>
            <button className="tai-cancel-btn" onClick={() => acceptFindPlacesOffer(false)}>
              <FontAwesomeIcon icon={solid("list-check")} />
              Let Me Pick
            </button>
            <button className="tai-primary-btn" onClick={() => acceptFindPlacesOffer(true)}>
              <FontAwesomeIcon icon={solid("wand-magic-sparkles")} />
              Auto-Build
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showCountryPicker) {
    return (
      <MultiSelectModal
        key="dest-screen-country"
        show={true}
        onHide={() => setShowCountryPicker(false)}
        MultiselectItems={(guideCities ? Object.keys(guideCities).sort((a, b) => a.localeCompare(b)) : countries).map((c, i) => ({ label: c, name: c, value: c, id: (i + 1).toString() }))}
        MultiselectSelectedItems={[]}
        MultiselectMode="uni"
        MultiselectCallback={onCountryPicked}
        MultiselectTitle={guideCities ? "Countries with a guide — select one" : "Where are you headed? Select a country"}
        MultiselectSearchText=""
      />
    );
  }

  if (showCityPicker) {
    const editingCountry = destinations[editingDestIdx]?.country;
    const cityList = guideCities
      ? (guideCities[editingCountry] || []).slice().sort((a, b) => a.localeCompare(b)).map((city) => ({ city }))
      : (citiesByCountry[editingCountry] || []).slice().sort((a, b) => b.popularity - a.popularity);
    const items = cityList.map((c, i) => ({ label: c.city, name: c.city, value: c.city, id: (i + 1).toString() }));
    if (!guideCities) items.push({ label: "Type a different city…", name: "other", value: OTHER_CITY_VALUE, id: (items.length + 1).toString() });
    return (
      <MultiSelectModal
        key="dest-screen-city"
        show={true}
        onHide={() => setShowCityPicker(false)}
        MultiselectItems={items}
        MultiselectSelectedItems={[]}
        MultiselectMode="uni"
        MultiselectCallback={onCityPicked}
        MultiselectTitle={guideCities ? `Cities in ${editingCountry} with a guide` : `Select a city in ${editingCountry}`}
        MultiselectSearchText=""
      />
    );
  }

  if (showCityTypePrompt) {
    return (
      <PromptModal
        show={true}
        onHide={() => setShowCityTypePrompt(false)}
        PromptModalTitle="City or Place"
        PromptModalMessage={`What city are you visiting in ${destinations[editingDestIdx]?.country}?`}
        PromptModalInputs={[{ name: "City", value: cityTypePromptValue }]}
        PromptModalButtons={[
          { Title: "Save", Method: saveTypedCity },
          { Title: "Cancel", Method: null },
        ]}
      />
    );
  }

  if (showExplore) {
    return <ExploreDestinationsFlow onApply={applyDestinationsList} onCancel={() => setShowExplore(false)} />;
  }

  if (showAI) {
    return <AIDestinationsFlow onApply={applyDestinationsList} onCancel={() => setShowAI(false)} />;
  }

  if (showScheduleImport) {
    return (
      <ScheduleDestinationsFlow
        sourceEvents={scheduleImportSourceEvents}
        onApply={applyDestinationsList}
        onCancel={() => { setShowScheduleImport(false); setScheduleImportSourceEvents(null); }}
      />
    );
  }

  if (showPasteDestinations) {
    return <PasteDestinationsFlow onApply={applyDestinationsList} onCancel={() => setShowPasteDestinations(false)} />;
  }

  if (showImportConfirmation) {
    return (
      <ImportConfirmationModal
        show={true}
        onHide={() => setShowImportConfirmation(false)}
        onImported={(createdEvents) => {
          setShowImportConfirmation(false);
          // Straight back to this screen's own list — no separate
          // checkbox-review step in between (that used to be
          // ScheduleDestinationsFlow, but this screen's own list already
          // lets the user remove/edit anything unwanted, making a second
          // review list redundant). Appended, not replaced — importing one
          // more confirmation email shouldn't wipe out destinations already
          // in the list.
          const candidates = deriveCandidatesFromEvents(createdEvents || []).map(cleanDestination).map(withNoPlace);
          if (candidates.length > 0) {
            setDestinations((prev) => [...prev, ...candidates.map((c, i) => ({ ...c, id: Date.now() + i }))]);
          }
        }}
      />
    );
  }

  return (
    <div className="modal">
      <Modal show={true} onHide={onHide} dialogClassName="tai-dialog" aria-labelledby="dest-screen-title" centered scrollable={false}>
        <Modal.Body style={{ padding: 0 }}>
          <div className="tai-shell">
            <div className="tai-header">
              <div className="tai-header-icon">
                <FontAwesomeIcon icon={solid("map-location-dot")} />
              </div>
              <h2 className="tai-title" id="dest-screen-title">Destinations</h2>
              <button className="tai-close" onClick={onHide}>
                <FontAwesomeIcon icon={solid("xmark")} />
              </button>
            </div>

            <div className="tai-scroll">
              {error !== "" && <p className="tai-paste-error">{error}</p>}

              <span className="tai-section-label">Destinations</span>
              {guideCities && (
                <p className="dest-screen-guides-note">
                  <FontAwesomeIcon icon={solid("circle-info")} /> Only countries and cities that have a guide are shown.
                </p>
              )}
              <p className="dest-screen-type-hint">
                <strong>No place</strong> = a placeholder day, e.g. "Sea Day".&nbsp;
                <strong>Skip search</strong> = a real place kept on the map, but not searched for attractions.
              </p>
              {destinations.map((dest, idx) => (
                <div key={dest.id} className="dest-screen-row">
                  <div className="dest-screen-reorder">
                    <button
                      className="dest-screen-reorder-btn"
                      disabled={idx === 0}
                      onClick={() => moveDestination(idx, -1)}
                    >
                      <FontAwesomeIcon icon={solid("chevron-up")} />
                    </button>
                    <button
                      className="dest-screen-reorder-btn"
                      disabled={idx === destinations.length - 1}
                      onClick={() => moveDestination(idx, 1)}
                    >
                      <FontAwesomeIcon icon={solid("chevron-down")} />
                    </button>
                  </div>
                  <div className="dest-screen-card-wrap">
                    {dest.noPlace ? (
                      <div className="tai-dest-card">
                        <div className="tai-dest-header">
                          <span className="tai-dest-num">{idx + 1}</span>
                          <label className="dest-screen-noplace-inline" title="Sea Day, Travel Day, etc. — a placeholder day with no place to search for">
                            <input
                              type="checkbox"
                              checked={!!dest.noPlace}
                              onChange={() => toggleNoPlace(idx)}
                            />
                            No place
                          </label>
                          <button className="tai-dest-delete" onClick={() => removeDestination(idx)}>
                            <FontAwesomeIcon icon={solid("xmark")} />
                          </button>
                        </div>
                        <div className="tai-input-row">
                          <span className="tai-input-label">Label</span>
                          <input
                            className="tai-input-field"
                            placeholder="Sea Day"
                            value={dest.country}
                            onChange={(e) => onChangeDestLabel(idx, e.target.value)}
                          />
                        </div>
                        <div className="tai-divider" />
                        <div className="tai-date-row">
                          <span className="tai-row-key">From</span>
                          <DatePicker showMonthDropdown showYearDropdown dropdownMode="select"
                            selected={Common.JSDateFromLuxonDate(dest.dateStart)}
                            onChange={(date) => onChangeDestStart(idx, date)}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={5}
                            timeCaption="time"
                            dateFormat="MMM d, yyyy HH:mm"
                            withPortal
                          />
                        </div>
                        <div className="tai-divider" />
                        <div className="tai-date-row">
                          <span className="tai-row-key">To</span>
                          <DatePicker showMonthDropdown showYearDropdown dropdownMode="select"
                            selected={Common.JSDateFromLuxonDate(dest.dateEnd)}
                            onChange={(date) => onChangeDestEnd(idx, date)}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={5}
                            timeCaption="time"
                            dateFormat="MMM d, yyyy HH:mm"
                            withPortal
                          />
                        </div>
                      </div>
                    ) : (
                      <DestinationCard
                        dest={dest}
                        index={idx}
                        showDelete={!datesOnly || destinations.length > 1}
                        placeLocked={datesOnly}
                        cityLabel="City"
                        showLabelField={!!dest.noSearch}
                        labelValue={dest.waypointLabel}
                        onChangeLabel={(value) => onChangeWaypointLabel(idx, value)}
                        headerExtra={datesOnly ? null :
                          <>
                            <label className="dest-screen-noplace-inline" title="Sea Day, Travel Day, etc. — a placeholder day with no place to search for">
                              <input
                                type="checkbox"
                                checked={!!dest.noPlace}
                                onChange={() => toggleNoPlace(idx)}
                              />
                              No place
                            </label>
                            <label className="dest-screen-noplace-inline" title="Keeps this as a real place on the map/schedule, but skips searching it for attractions — e.g. a fuel stop or overnight stay">
                              <input
                                type="checkbox"
                                checked={!!dest.noSearch}
                                onChange={() => toggleNoSearch(idx)}
                              />
                              Skip search
                            </label>
                          </>
                        }
                        onSelectCountry={() => openCountryPicker(idx)}
                        onSelectCity={() => openCityPicker(idx)}
                        onChangeStart={(date) => onChangeDestStart(idx, date)}
                        onChangeEnd={(date) => onChangeDestEnd(idx, date)}
                        onRemove={() => removeDestination(idx)}
                      />
                    )}
                  </div>
                </div>
              ))}

              {datesOnly && (
                <p className="bmtw-dest-hint">The cities come from this guide's places — change the dates, or remove a city you don't want in this trip.</p>
              )}
              {!datesOnly && <div className="tai-dest-btn-row">
                <button className="tai-add-dest-btn" onClick={addDestination}>
                  <FontAwesomeIcon icon={solid("plus")} />
                  Add Destination
                </button>
                {/* The other ways of adding destinations can all bring in a city
                    with no guide — left out when building from guides
                    (guideCities), where every destination must have one. */}
                {!guideCities && (<>
                <button className="tai-paste-dest-btn" onClick={() => setShowPasteDestinations(true)}>
                  <FontAwesomeIcon icon={solid("clipboard")} />
                  Paste Destinations
                </button>
                <button className="tai-paste-dest-btn" onClick={() => { setScheduleImportSourceEvents(null); setShowScheduleImport(true); }}>
                  <FontAwesomeIcon icon={solid("calendar-days")} />
                  Destinations From Schedule
                </button>
                <button className="tai-paste-dest-btn" onClick={() => setShowImportConfirmation(true)}>
                  <FontAwesomeIcon icon={solid("envelope-open-text")} />
                  Destinations from Confirmation
                </button>
                <button className="tai-paste-dest-btn" onClick={() => setShowExplore(true)}>
                  <FontAwesomeIcon icon={solid("earth-americas")} />
                  Explore Destinations
                </button>
                <button className="tai-paste-dest-btn" onClick={() => setShowAI(true)}>
                  <FontAwesomeIcon icon={solid("wand-magic-sparkles")} />
                  Destinations from AI
                </button>
                </>)}
                {destinations.length > 0 && (
                  <button className="tai-paste-dest-btn dest-screen-delete-all-btn" onClick={removeAllDestinations}>
                    <FontAwesomeIcon icon={solid("trash")} />
                    Delete All
                  </button>
                )}
              </div>}
            </div>

            <div className="tai-paste-actions dest-screen-footer">
              <button className="tai-cancel-btn" onClick={onHide}>Cancel</button>
              <button className="tai-primary-btn" onClick={handleSave}>
                <FontAwesomeIcon icon={solid("check")} />
                Save
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DestinationsScreen;
